/* eslint-disable react/prop-types */
/**
 * /feedback — the bug / feature request queue.
 *
 * ONE screen, two audiences, because the server already draws the line:
 *   * BD.Admin sees every ticket and gets the triage controls (status, kind,
 *     severity, a status note, internal-only comments) and the Microsoft
 *     Clarity correlation panel.
 *   * Everyone else sees only the tickets they filed, and can read replies
 *     and answer them.
 * Splitting this into two pages would duplicate the whole detail drawer to
 * hide four form controls, and would give a reporter nowhere to read the
 * answer to their own ticket — which is the thing that makes people file a
 * second one.
 *
 * The gating here is UI/UX only. `GET /feedback_requests` is scoped
 * server-side to the caller and `PUT` is admin-only, so a non-admin who
 * reaches this route sees their own tickets and no triage controls — there is
 * nothing to redirect away from, and unlike the Admin -> * screens this route
 * is deliberately open to everyone.
 *
 * WHY THE CLARITY PANEL IS THE POINT
 * Each ticket carries the reporter's browser timestamp, timezone, Clarity
 * session id and a link into the Clarity dashboard filtered to that day. An
 * admin triaging a bug should not be reading a prose description of what
 * happened — they should be watching it. The panel also shows the gap between
 * the browser clock and the server clock, because a reporter with a skewed
 * clock will otherwise send you hunting through the wrong hour of recordings.
 *
 * Deep-linked as `/feedback?request=<id>` — that is the `link_url` the
 * notification bell carries, so clicking a "new bug report" notification
 * lands directly on the ticket.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  BugOutlined,
  BulbOutlined,
  DownloadOutlined,
  LockOutlined,
  MailOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
  WigFilterPanel,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import useIsAdmin from "../../hooks/useIsAdmin";
import appSupportService from "../../services/appSupportService";
import FeedbackFormModal from "../../components/feedbackFormModal";
import FeedbackKpiHeader from "../../components/feedbackKpiHeader";
import { apiErrorText } from "../../utils/apiError";
import { applyColumnMetadata } from "../../utils/applyColumnMetadata";
import metadataAdminService from "../../services/metadataAdminService";
import { sourceByKey } from "../../config/metadataAdmin";
import { clockSkewSeconds } from "../../utils/clientContext";
import { buildFeedbackFilterFields } from "../../utils/feedbackFilterFields";
import { filterFieldsToSearchFields } from "../../utils/filterFields";
import { filterRows } from "../../utils/filterQuery";
import { isOpenTicket } from "../../utils/feedbackStats";
import {
  FEEDBACK_FILTERS_COLLAPSED,
  readFlag,
  writeFlag,
} from "../../utils/uiPrefs";

const { Title, Text, Paragraph } = Typography;

/** The app_support table these columns describe. */
const FEEDBACK_TABLE = "app_feedback_requests";

/**
 * Columns whose display name differs from the database column behind them.
 *
 * Replay is a button built from `clarity_replay_url` and has no `dataIndex`
 * at all. Stated here so the rename is visible where it happens, rather than
 * implied by a lookup that would silently miss.
 *
 * The reporter column needs NO entry: it is keyed `reported_by`, matching the
 * database column directly. That was deliberate — the CSV export reads
 * `row[dataIndex]`, so keying it on the resolved display name exported the
 * wrong half. The template still carries `reported_by_name` there, which is
 * why its map has one more entry than this one.
 */
const COLUMN_FIELDS = {
  replay: "clarity_replay_url",
};
const { TextArea } = Input;

// In workflow order, which is the order the triage dropdown offers them.
// `retest` is the one status whose next move belongs to the reporter: the fix
// is deployed and someone has to confirm it against the thing that broke. It
// is not terminal — the ticket stays open and keeps ageing until they say it
// worked, because "it should work now" is not the same as "it works".
const STATUS_META = {
  new: { label: "New", color: "blue" },
  triaged: { label: "Triaged", color: "cyan" },
  in_progress: { label: "In progress", color: "gold" },
  retest: { label: "Ready to re-test", color: "purple" },
  resolved: { label: "Resolved", color: "green" },
  wont_fix: { label: "Won't fix", color: "default" },
  duplicate: { label: "Duplicate", color: "default" },
};

const KIND_META = {
  bug: { label: "Bug", color: "red", icon: <BugOutlined /> },
  feature: { label: "Feature", color: "purple", icon: <BulbOutlined /> },
  question: { label: "Question", color: "blue", icon: <QuestionCircleOutlined /> },
};

const SEVERITY_META = {
  low: { label: "Low", color: "default" },
  medium: { label: "Medium", color: "blue" },
  high: { label: "High", color: "orange" },
  critical: { label: "Critical", color: "red" },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, m]) => ({
  value,
  label: m.label,
}));
const KIND_OPTIONS = Object.entries(KIND_META).map(([value, m]) => ({
  value,
  label: m.label,
}));
const SEVERITY_OPTIONS = Object.entries(SEVERITY_META).map(([value, m]) => ({
  value,
  label: m.label,
}));

const EMPTY_QUERY = { combinator: "and", rules: [] };

/**
 * The list endpoint caps at 1000. Feedback is tens of tickets, not thousands,
 * so the whole scoped queue fits in one call — which is what lets the KPI
 * tiles and the filter chips both run off one fetch.
 */
const LIST_LIMIT = 1000;

const fmtDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/**
 * ClarityPanel — the "go watch it happen" block on a ticket.
 *
 * Shows the reporter's browser-side timestamp and zone, the Clarity session
 * id, and a link into the dashboard filtered to that day. The skew line only
 * appears when the two clocks disagree by more than a minute, which is the
 * point at which searching by time starts to mislead.
 */
const ClarityPanel = ({ row }) => {
  const skew = clockSkewSeconds(row?.client_reported_at, row?.created_at);
  const skewWorthMentioning = skew !== null && Math.abs(skew) > 60;

  const items = [
    {
      key: "who",
      label: "Reported by",
      children: row?.reported_by_name
        ? `${row.reported_by_name} <${row.reported_by}>`
        : row?.reported_by || "—",
    },
    {
      key: "browser",
      label: "Reported (their clock)",
      children: fmtDateTime(row?.client_reported_at) || "—",
    },
    {
      key: "server",
      label: "Received (server clock)",
      children: fmtDateTime(row?.created_at) || "—",
    },
    {
      key: "tz",
      label: "Their timezone",
      children: row?.client_timezone
        ? `${row.client_timezone}${
            row.client_tz_offset_minutes != null
              ? ` (UTC${row.client_tz_offset_minutes >= 0 ? "+" : ""}${
                  row.client_tz_offset_minutes / 60
                })`
              : ""
          }`
        : "—",
    },
    {
      key: "session",
      label: "Clarity session",
      children: row?.clarity_session_id || "not recorded",
    },
    { key: "page", label: "Page", children: row?.page_url || "—" },
    { key: "route", label: "Route", children: row?.app_route || "—" },
    { key: "version", label: "App version", children: row?.app_version || "—" },
    { key: "viewport", label: "Screen", children: row?.viewport || "—" },
    { key: "ua", label: "Browser", children: row?.user_agent || "—" },
    {
      key: "roles",
      label: "Their roles at the time",
      children: row?.reported_by_roles || "—",
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 8 }} wrap>
        {row?.clarity_replay_url ? (
          <Button
            type="primary"
            ghost
            icon={<PlayCircleOutlined />}
            href={row.clarity_replay_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open session replay
          </Button>
        ) : (
          <Tooltip title="Clarity wasn't running for this reporter, so there's no recording to open.">
            <Button icon={<PlayCircleOutlined />} disabled>
              No session replay
            </Button>
          </Tooltip>
        )}
      </Space>

      {skewWorthMentioning && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          title="This reporter's clock is off"
          description={
            `Their browser was ${Math.abs(skew)}s ${
              skew > 0 ? "ahead of" : "behind"
            } the server. Search the recording by their clock, not ours.`
          }
        />
      )}

      <Descriptions
        size="small"
        column={1}
        items={items.map((i) => ({
          ...i,
          children: (
            <Text style={{ fontSize: 12, wordBreak: "break-all" }}>
              {i.children}
            </Text>
          ),
        }))}
      />
    </>
  );
};

const Feedback = ({ darkMode }) => {
  const { theme, token, getToken, isTokenExpired, user } =
    useContext(AppContext);
  const { isAdmin } = useIsAdmin();
  const [ref, bounds] = useMeasure();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [openOnly, setOpenOnly] = useState(true);
  // Admins only: "everything" vs "just mine". Reporters are already narrowed
  // by the server, so the control is meaningless for them.
  const [scope, setScope] = useState("all");

  // Applied filter-chip query, evaluated in the browser by `filterRows`.
  // There is no POST /feedback_requests/search to forward it to, and there
  // does not need to be: the whole scoped queue is already here.
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [filtersCollapsed, setFiltersCollapsed] = useState(() =>
    readFlag(FEEDBACK_FILTERS_COLLAPSED, false)
  );
  const handleFiltersCollapsed = useCallback((next) => {
    setFiltersCollapsed(next);
    writeFlag(FEEDBACK_FILTERS_COLLAPSED, next);
  }, []);

  const [reportOpen, setReportOpen] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyInternal, setReplyInternal] = useState(false);
  const [replying, setReplying] = useState(false);
  const [savingTriage, setSavingTriage] = useState(false);
  // Bumped when a triage save fails, to remount the triage form from `detail`.
  // The controls write straight to the server on change, so a rejected save
  // otherwise leaves the dropdown showing a status nobody stored — which is
  // how a proxy dropping `?app=` read as "the status changed, and also there
  // was an error".
  const [triageNonce, setTriageNonce] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  /**
   * Fetches the WHOLE scoped queue — deliberately without `open_only`.
   *
   * The checkbox now narrows the grid client-side instead. The KPI tiles have
   * to count resolved and won't-fix tickets to say anything useful about the
   * open ones, and re-fetching every time someone ticks a box to get numbers
   * that then disagree with the grid is the worse trade. `scope` still goes
   * to the server, because row visibility is the server's call.
   */
  const loadList = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      const resp = await appSupportService.listFeedback({
        token: tk,
        userOid,
        limit: LIST_LIMIT,
        mine: isAdmin && scope === "mine" ? true : undefined,
      });
      if (mountedRef.current) setRows(resp?.data?.data || []);
    } catch (err) {
      console.error("Failed to load feedback:", err?.message || err);
      if (mountedRef.current) {
        setErrorMsg(
          apiErrorText(err, { fallback: "Couldn't load the request list." })
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [getAuth, isAdmin, scope]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // The grid's rows: the scoped queue, narrowed by the Open-only checkbox and
  // then by the applied chips. The KPI strip reads `rows` (everything) so its
  // denominators do not move when either narrowing changes.
  const displayRows = useMemo(() => {
    const scoped = openOnly ? rows.filter(isOpenTicket) : rows;
    return filterRows(scoped, query);
  }, [rows, openOnly, query]);

  const filterFields = useMemo(
    () =>
      buildFeedbackFilterFields({
        statusMeta: STATUS_META,
        kindMeta: KIND_META,
        severityMeta: SEVERITY_META,
        isAdmin,
      }),
    [isAdmin]
  );
  const advancedFields = useMemo(
    () => filterFieldsToSearchFields(filterFields),
    [filterFields]
  );

  const handleFilterReset = useCallback(() => setQuery(EMPTY_QUERY), []);

  const loadDetail = useCallback(
    async (id) => {
      setDetailLoading(true);
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await appSupportService.getFeedback({
          token: tk,
          userOid,
          id,
        });
        if (mountedRef.current) setDetail(resp?.data?.data || null);
      } catch (err) {
        console.error("Failed to load request:", err?.message || err);
        if (mountedRef.current) {
          // A 404 here is "yours or nobody's" — do not dress it up as
          // something the user can fix by retrying.
          message.error(
            apiErrorText(err, { fallback: "Couldn't open that request." })
          );
          setDetail(null);
        }
      } finally {
        if (mountedRef.current) setDetailLoading(false);
      }
    },
    [getAuth]
  );

  // Deep link: /feedback?request=<id> is the notification bell's landing spot.
  const requestParam = searchParams.get("request");
  useEffect(() => {
    if (requestParam && String(detail?.id) !== String(requestParam)) {
      loadDetail(requestParam);
    }
    if (!requestParam && detail) setDetail(null);
    // `detail` is deliberately not a dependency — including it would reopen
    // the drawer every time a reply refreshed the ticket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParam, loadDetail]);

  const openTicket = useCallback(
    (row) => {
      const next = new URLSearchParams(searchParams);
      next.set("request", String(row.id));
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const closeTicket = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("request");
    setSearchParams(next, { replace: false });
    setDetail(null);
    setReplyBody("");
    setReplyInternal(false);
  }, [searchParams, setSearchParams]);

  const handleTriage = useCallback(
    async (updates) => {
      if (!detail?.id) return;
      setSavingTriage(true);
      try {
        const { token: tk, userOid } = await getAuth();
        await appSupportService.updateFeedback({
          token: tk,
          userOid,
          id: detail.id,
          updates,
        });
        await loadDetail(detail.id);
        loadList();
        message.success("Updated.");
      } catch (err) {
        console.error("Failed to update request:", err?.message || err);
        message.error(
          apiErrorText(err, { fallback: "Couldn't save that change." })
        );
        // Put the controls back to what the server actually holds.
        if (mountedRef.current) setTriageNonce((n) => n + 1);
      } finally {
        if (mountedRef.current) setSavingTriage(false);
      }
    },
    [detail?.id, getAuth, loadDetail, loadList]
  );

  const handleReply = useCallback(async () => {
    if (!detail?.id || !replyBody.trim()) return;
    setReplying(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.addFeedbackComment({
        token: tk,
        userOid,
        id: detail.id,
        body: replyBody,
        isInternal: isAdmin && replyInternal,
      });
      setReplyBody("");
      setReplyInternal(false);
      await loadDetail(detail.id);
      loadList();
    } catch (err) {
      console.error("Failed to post reply:", err?.message || err);
      message.error(
        apiErrorText(err, { fallback: "Couldn't post that reply." })
      );
    } finally {
      if (mountedRef.current) setReplying(false);
    }
  }, [detail?.id, replyBody, replyInternal, isAdmin, getAuth, loadDetail, loadList]);

  const handleDownload = useCallback(
    async (attachment) => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await appSupportService.getFeedbackAttachmentUrl({
          token: tk,
          userOid,
          id: attachment.id,
        });
        const url = resp?.data?.data?.url;
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        else message.error("Couldn't get a download link for that file.");
      } catch (err) {
        console.error("Failed to get attachment url:", err?.message || err);
        message.error(
          apiErrorText(err, { fallback: "Couldn't download that file." })
        );
      }
    },
    [getAuth]
  );

  const columns = useMemo(
    () => [
      {
        dataIndex: "id",
        title: "#",
        key: "id",
        width: 70,
        sorter: (a, b) => Number(a?.id) - Number(b?.id),
      },
      {
        dataIndex: "kind",
        title: "Type",
        key: "kind",
        width: 110,
        render: (v) => {
          const m = KIND_META[v];
          return m ? (
            <Tag color={m.color} icon={m.icon}>
              {m.label}
            </Tag>
          ) : (
            v
          );
        },
      },
      {
        dataIndex: "title",
        title: "Summary",
        key: "title",
        width: 280,
        // Free text: a generated dropdown would list one entry per ticket,
        // which is the data read back rather than a filter. The chips above
        // do "Summary contains" properly.
        filterable: false,
        render: (v) => <Text strong>{v}</Text>,
      },
      {
        // The reporter's own words. On screen it is one clipped line with the
        // full text on hover — the drawer is where you read a ticket. It is
        // in the grid because the CSV export is built from the COLUMN LIST,
        // not from the row objects (see WigAntTable.exportTable), so a field
        // that is not a column cannot be exported, and an export of a bug
        // queue with the descriptions missing is an export of nothing. It
        // also makes the description searchable by the chips above.
        dataIndex: "description",
        title: "Details",
        key: "description",
        ellipsis: { showTitle: false },
        // Same as Summary, more so — these are whole paragraphs.
        filterable: false,
        render: (v) =>
          v ? (
            <Tooltip
              title={
                <span style={{ whiteSpace: "pre-wrap" }}>{v}</span>
              }
              styles={{ root: { maxWidth: 520 } }}
            >
              <Text type="secondary">{v}</Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        dataIndex: "status",
        title: "Status",
        key: "status",
        width: 130,
        render: (v) => {
          const m = STATUS_META[v];
          return m ? <Tag color={m.color}>{m.label}</Tag> : v;
        },
      },
      {
        dataIndex: "severity",
        title: "Severity",
        key: "severity",
        width: 110,
        render: (v) => {
          if (!v) return <Text type="secondary">—</Text>;
          const m = SEVERITY_META[v];
          return m ? <Tag color={m.color}>{m.label}</Tag> : v;
        },
      },
      // Who filed it only matters when you can see other people's.
      ...(isAdmin
        ? [
            {
              // Keyed on the EMAIL, not the display name (feedback #66).
              //
              // The CSV export is built from the column list and takes
              // `row[dataIndex]` raw — the render never runs (see
              // WigAntTable.exportTable). So whatever sits here is what an
              // export of the queue says about who reported what.
              //
              // `reported_by_name` was the wrong half twice over: it is
              // optional, and until the de-hub fix that shipped alongside this
              // it held the token's `appid` — one GUID identical for every
              // user of the app, which is what an export of 60 tickets from
              // three people actually contained. The email is always present
              // and actually identifies someone, so it is the column; the
              // name rides along in the render when there is one.
              dataIndex: "reported_by",
              title: "Reported by",
              key: "reported_by",
              width: 220,
              // Name AND email. Showing `name || email` meant that as soon as
              // a display name existed the address vanished, so an admin
              // triaging a ticket had a person's name and no way to reach
              // them. The email is the actionable half.
              render: (_v, row) => (
                <Space direction="vertical" size={0}>
                  {row?.reported_by_name ? (
                    <Text>{row.reported_by_name}</Text>
                  ) : null}
                  {row?.reported_by ? (
                    <Text
                      type="secondary"
                      copyable={{ text: row.reported_by }}
                      style={{ fontSize: 11 }}
                    >
                      {row.reported_by}
                    </Text>
                  ) : null}
                </Space>
              ),
            },
          ]
        : []),
      {
        dataIndex: "comment_count",
        title: "Replies",
        key: "comment_count",
        width: 90,
        align: "center",
        render: (v) => (v ? v : <Text type="secondary">—</Text>),
      },
      {
        // The BROWSER-side stamp, deliberately: it is what lines up with a
        // Clarity recording, and it is what the reporter would recognise.
        dataIndex: "client_reported_at",
        title: "Reported",
        key: "client_reported_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.client_reported_at ?? a?.created_at ?? "").localeCompare(
            String(b?.client_reported_at ?? b?.created_at ?? "")
          ),
        render: (v, row) => fmtDateTime(v || row?.created_at),
      },
      ...(isAdmin
        ? [
            {
              title: "Replay",
              key: "replay",
              width: 90,
              align: "center",
              render: (_, row) =>
                row?.clarity_replay_url ? (
                  <Tooltip title="Open the Clarity session recording">
                    <Button
                      size="small"
                      type="link"
                      icon={<PlayCircleOutlined />}
                      href={row.clarity_replay_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
          ]
        : []),
    ],
    [isAdmin]
  );

  /**
   * Presentation settings for these columns, from the shared app_support
   * metadata table — labels, widths, order, visibility, filtering. Editable on
   * Admin → Field Settings rather than requiring a release in every app that
   * embeds this grid (wig-broker-deals#189).
   */
  const [columnMeta, setColumnMeta] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await metadataAdminService.listMetadata({
          token: tk,
          userOid,
          tableName: FEEDBACK_TABLE,
          source: sourceByKey("app_support"),
        });
        if (!cancelled) setColumnMeta(resp?.data?.data || []);
      } catch (err) {
        // Deliberately silent, and deliberately not an error state. The
        // metadata lives in a different de-hub module and database; if it is
        // unreachable — or the migration has not been applied yet — the grid
        // renders exactly as it did before any of this existed. A shared admin
        // table being down must not take a user's feedback queue with it.
        console.debug(
          "Feedback column metadata unavailable; using built-in defaults:",
          err?.message || err
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAuth]);

  const displayColumns = useMemo(
    () => applyColumnMetadata(columns, columnMeta, { alias: COLUMN_FIELDS }),
    [columns, columnMeta]
  );

  // 220 was sized for a title row and a grid. The KPI strip and the filter
  // row each take a band above the table now, so the grid has to give the
  // same amount back or it runs off the bottom of the viewport.
  const gridHeight = Math.max(bounds.height - 330, 320);
  const heading = isAdmin ? "Feedback" : "My requests";

  const detailComments = detail?.comments || [];
  const detailAttachments = detail?.attachments || [];

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark" || darkMode}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    {heading}
                  </Title>
                  <Space wrap>
                    {isAdmin && (
                      <Segmented
                        value={scope}
                        onChange={setScope}
                        options={[
                          { label: "Everyone", value: "all" },
                          { label: "Mine", value: "mine" },
                        ]}
                      />
                    )}
                    <Checkbox
                      checked={openOnly}
                      onChange={(e) => setOpenOnly(e.target.checked)}
                    >
                      Open only
                    </Checkbox>
                    <Button icon={<ReloadOutlined />} onClick={loadList}>
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setReportOpen(true)}
                    >
                      New request
                    </Button>
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    title="Failed to load requests"
                    description={errorMsg}
                  />
                )}

                {(loading || rows.length > 0) && !errorMsg && (
                  <>
                    {/* Counts the WHOLE scoped queue, not the filtered view —
                        that is the point of them. */}
                    <FeedbackKpiHeader
                      rows={rows}
                      visibleCount={displayRows.length}
                      loading={loading}
                      errorMsg={errorMsg}
                      onRefresh={loadList}
                    />
                    {/* The same filter surface as the deals list, applied in
                        the browser: chips for the common questions ("status
                        is New", "type is Bug"), the query builder behind
                        Advanced for the rest, and free text over the summary,
                        the details and the status note. */}
                    <div style={{ width: "99%", padding: "4px 0" }}>
                      <WigFilterPanel
                        layout="inline"
                        fields={filterFields}
                        advancedFields={advancedFields}
                        // EMPTY_QUERY, not the live `query`. The panel's
                        // Reset restores to whatever `initialQuery` holds at
                        // that moment, so feeding it the applied query would
                        // make Reset reset to the filter you are trying to
                        // clear. The page owns the applied query; the panel
                        // always starts, and returns to, empty.
                        initialQuery={EMPTY_QUERY}
                        onSearch={setQuery}
                        onReset={handleFilterReset}
                        collapsible
                        collapsed={filtersCollapsed}
                        onCollapsedChange={handleFiltersCollapsed}
                        resetLabel="Clear all"
                        // Just the result count — the KPI strip above already
                        // says how it relates to the whole queue.
                        status={
                          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                            {loading
                              ? "Loading requests…"
                              : `${displayRows.length} request${
                                  displayRows.length === 1 ? "" : "s"
                                } shown`}
                          </Text>
                        }
                      />
                    </div>
                  </>
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    title={
                      isAdmin ? "Nothing in the queue" : "You haven't filed anything yet"
                    }
                    description={
                      isAdmin
                        ? "No feedback requests have been filed yet."
                        : "Hit New request — or the Send feedback item in the user menu — to report a problem or ask for a feature."
                    }
                  />
                ) : (
                  <WigAntTable
                    dataSource={displayRows}
                    columns={displayColumns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    // The filter panel owns filtering now, as on the deals
                    // list. WigAntTable's own search box keeps a second,
                    // private copy of the row set and resets it whenever
                    // `dataSource` changes — so with both on, editing a chip
                    // silently wiped whatever was typed in the box. Free text
                    // is a "Summary contains" / "Details contains" chip.
                    showSearch={false}
                    // Column filters are ON, and said per column: Summary
                    // and Details carry `filterable: false` because their
                    // distinct values are one paragraph per ticket. The other
                    // eight columns get the filter they should always have
                    // had — this used to be `enableColumnFilters={false}`,
                    // which threw out eight to fix two.
                    showActionButton={false}
                    showExport={isAdmin}
                    exportOptions={{ filename: "feedback-requests" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                    onRow={(record) => ({
                      onClick: () => openTicket(record),
                      style: { cursor: "pointer" },
                    })}
                  />
                )}
              </Space>
            }
          />
        </Row>

        <Drawer
          open={Boolean(requestParam)}
          onClose={closeTicket}
          width={720}
          title={
            detail ? (
              <Space wrap>
                <Text strong>#{detail.id}</Text>
                {KIND_META[detail.kind] && (
                  <Tag
                    color={KIND_META[detail.kind].color}
                    icon={KIND_META[detail.kind].icon}
                  >
                    {KIND_META[detail.kind].label}
                  </Tag>
                )}
                {STATUS_META[detail.status] && (
                  <Tag color={STATUS_META[detail.status].color}>
                    {STATUS_META[detail.status].label}
                  </Tag>
                )}
              </Space>
            ) : (
              "Request"
            )
          }
        >
          {detailLoading && !detail ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin />
            </div>
          ) : !detail ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="This request isn't available."
            />
          ) : (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <div>
                <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
                  {detail.title}
                </Title>
                <Space size={6} wrap>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {detail.reported_by_name || detail.reported_by}
                  </Text>
                  {/* The address, always, and as a mailto — the reply thread
                      covers most cases, but "I need to ring/email this
                      person" is exactly when an admin opens the ticket, and
                      name-only left them with nothing to act on. Shown even
                      when it duplicates the line above, so the contact
                      details are never conditional. */}
                  {detail.reported_by ? (
                    <Typography.Link
                      href={`mailto:${detail.reported_by}?subject=${encodeURIComponent(
                        `Re: ${detail.title} (feedback #${detail.id})`
                      )}`}
                      style={{ fontSize: 12 }}
                    >
                      <MailOutlined /> {detail.reported_by}
                    </Typography.Link>
                  ) : null}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    · {fmtDateTime(detail.client_reported_at || detail.created_at)}
                  </Text>
                </Space>
              </div>

              <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                {detail.description}
              </Paragraph>

              {detail.status_note && (
                <Alert
                  type="info"
                  showIcon
                  title="Where this stands"
                  description={detail.status_note}
                />
              )}

              {detailAttachments.length > 0 && (
                <>
                  <Divider orientation="left" style={{ margin: "4px 0" }}>
                    Attachments
                  </Divider>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    {detailAttachments.map((a) => (
                      <Button
                        key={a.id}
                        type="link"
                        icon={<DownloadOutlined />}
                        style={{ paddingLeft: 0 }}
                        onClick={() => handleDownload(a)}
                      >
                        {a.filename}
                      </Button>
                    ))}
                  </Space>
                </>
              )}

              {isAdmin && (
                <>
                  <Divider orientation="left" style={{ margin: "4px 0" }}>
                    Triage
                  </Divider>
                  <Form
                    layout="inline"
                    style={{ rowGap: 8 }}
                    initialValues={{
                      status: detail.status,
                      kind: detail.kind,
                      severity: detail.severity,
                    }}
                    key={`triage-${detail.id}-${detail.status}-${detail.kind}-${detail.severity}-${triageNonce}`}
                  >
                    <Form.Item label="Status" name="status">
                      <Select
                        style={{ width: 150 }}
                        options={STATUS_OPTIONS}
                        disabled={savingTriage}
                        onChange={(v) => handleTriage({ status: v })}
                      />
                    </Form.Item>
                    <Form.Item label="Type" name="kind">
                      <Select
                        style={{ width: 130 }}
                        options={KIND_OPTIONS}
                        disabled={savingTriage}
                        onChange={(v) => handleTriage({ kind: v })}
                      />
                    </Form.Item>
                    <Form.Item label="Severity" name="severity">
                      {/* No `allowClear`: FeedbackUpdate treats a null field
                          as "leave it alone", so there is no way to unset a
                          severity — offering a clear button that silently
                          does nothing is worse than not offering one. */}
                      <Select
                        style={{ width: 130 }}
                        options={SEVERITY_OPTIONS}
                        disabled={savingTriage}
                        onChange={(v) => handleTriage({ severity: v })}
                      />
                    </Form.Item>
                  </Form>
                  <Input.Search
                    placeholder="Status note — shown to the reporter"
                    enterButton="Save note"
                    defaultValue={detail.status_note || ""}
                    loading={savingTriage}
                    onSearch={(v) => handleTriage({ status_note: v })}
                  />

                  <Divider orientation="left" style={{ margin: "4px 0" }}>
                    Session context
                  </Divider>
                  <ClarityPanel row={detail} />
                </>
              )}

              <Divider orientation="left" style={{ margin: "4px 0" }}>
                Conversation
              </Divider>

              {detailComments.length === 0 ? (
                <Text type="secondary">No replies yet.</Text>
              ) : (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  {detailComments.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        padding: 8,
                        borderRadius: 6,
                        background: c.is_internal
                          ? "rgba(250,173,20,0.10)"
                          : "rgba(22,104,220,0.06)",
                      }}
                    >
                      <Space size={6} style={{ marginBottom: 2 }}>
                        <Text strong style={{ fontSize: 12 }}>
                          {c.author_name || c.author_email}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {fmtDateTime(c.created_at)}
                        </Text>
                        {c.is_internal && (
                          <Tag color="orange" icon={<LockOutlined />}>
                            Internal
                          </Tag>
                        )}
                      </Space>
                      <div style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
                    </div>
                  ))}
                </Space>
              )}

              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <TextArea
                  rows={3}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder={
                    isAdmin
                      ? "Reply to the reporter…"
                      : "Add more detail, or answer a question…"
                  }
                />
                <Space>
                  <Button
                    type="primary"
                    loading={replying}
                    disabled={!replyBody.trim()}
                    onClick={handleReply}
                  >
                    {replyInternal ? "Save internal note" : "Send reply"}
                  </Button>
                  {isAdmin && (
                    <Tooltip title="An internal note is visible only to admins and sends no notification.">
                      <Checkbox
                        checked={replyInternal}
                        onChange={(e) => setReplyInternal(e.target.checked)}
                      >
                        Internal note
                      </Checkbox>
                    </Tooltip>
                  )}
                </Space>
              </Space>
            </Space>
          )}
        </Drawer>

        <FeedbackFormModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          onSubmitted={() => loadList()}
        />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Feedback;
